import { useContext, useEffect, useState } from "react";

import { GlobalContext } from "../contextProvider/ContextProvider";
import ActionButton from "../formInputs/buttons/ActionButton";
import Hamburger from "./Hamburger.jsx";
import HelpfulLinks from "./HelpfulLinks.jsx";
import ResourcesLinks from "./ResourcesLinks.jsx";
import Icon from "../icon/Icon";
import RbA from "../rba/RbA";
import T from "../translations/Translations";

import { DASH_LINKS } from "../../const/AppCardLinks.jsx";
import { EMPLOYEE_DASHBOARD, GUARDIAN_DASHBOARD } from "../../utils/auth/config";
import { hasRole } from "../../utils/isUser";
import { isUserDistrictAdmin } from "../../utils/isUser.jsx";
import { logout } from "../../utils/auth/Auth";
import {
    SetActionWindows,
    SetAllLocationDtos,
    SetCurrentTimePeriodDto,
    SetDistrictTemplateDto,
    SetSchoolTemplateDto,
    SetSchoolYearDto,
    SetTimePeriodDto
} from "../contextProvider/ContextSetter.jsx";

import "../../styles/Header.scss";

/**
 * This is returning the header content with buttons and links
 * @name Header
 * @return {JSX.Element}
 */
const Header = () => {
    const { state } = useContext(GlobalContext);
    const { userDetails } = state || {};
    const { category, givenName, location, surname } = userDetails || {};

    const [isDistrict, setIsDistrict] = useState(false);
    const [isGuardian, setIsGuardian] = useState(false);

    const globalDashboard = T({ key: "Global_Dashboard" });
    const navLogout = T({ key: "Main_Nav_LogOut" });
    const welcome = T({ key: "Main_Nav_Welcome" });

    /**
     * Set the SchoolYearDto in context
     */
    SetSchoolYearDto();

    /**
     * Set the SetTimePeriodDto in context
     */
    SetTimePeriodDto();

    /**
     * Set the SetTimePeriodDto in context
     */
    SetCurrentTimePeriodDto();

    /**
     * Set the SetDistrictTemplateDto in context
     */
    SetDistrictTemplateDto();

    /**
     * Set the SetSchoolTemplateDto in context
     */
    SetSchoolTemplateDto();

    /**
     * Set the all elementary schools in context
     */
    SetAllLocationDtos();

    /**
     * Set the action control windows
     */
    SetActionWindows();

    const dashboard = (e) => {
        if (
            e.type.toLowerCase() === "click" ||
            (e.type.toLowerCase() === "keydown" && e.key?.toLowerCase() === "enter")
        ) {
            globalThis.location.href = GUARDIAN_DASHBOARD;
        }
    };

    const getLocationShortName = () => {
        return userDetails?.userAttributeDto?.userAttributeMap?.CURRENT_PREDOMINANT_SCHOOL?.shortName || "";
    };

    /* Determine if user is district admin */
    useEffect(() => {
        if (isUserDistrictAdmin(userDetails)) {
            setIsDistrict(true);
        }
    }, [userDetails]);

    useEffect(() => {
        if (userDetails) {
            setIsGuardian(hasRole(userDetails, ["GUARDIAN"]));
        }
    }, [userDetails]);

    const diplayLocation = () => {
        if (isDistrict) {
            return location;
        }
        return getLocationShortName();
    };

    /**
     * Allow the User to "close" the session and window
     * @name closeTab
     */
    const closeTab = () => {
        // remove all sessionStorage
        sessionStorage.clear();
        window.opener = null;
        window.open(EMPLOYEE_DASHBOARD, "_self");
        window.close();
    };

    return (
        <RbA
            allowedRoles={[
                "EPR_ADMIN",
                "PK12",
                "GUARDIAN",
                "PRINCIPAL",
                "ASSISTANT_PRINCIPAL",
                "REGISTRAR",
                "SDIS",
                "TEACHER"
            ]}
            redirect="/notFound"
        >
            <nav className={`header-container`}>
                {userDetails && (
                    <>
                        <div className="header-name-container">
                            <a
                                aria-label="Guardian Dashboard Page Link"
                                href={isGuardian ? DASH_LINKS.guardian : EMPLOYEE_DASHBOARD}
                                rel="noopener noreferrer"
                                style={{
                                    textDecoration: "none",
                                    color: "black"
                                }}
                            >
                                <Icon className="header-dcsd-icon" height={42} iconName="DCSD" width={42} />
                            </a>
                            <h5>
                                {welcome} {givenName} {surname}
                            </h5>
                        </div>
                        {isGuardian ? (
                            <div className="header-right">
                                <div className="nav-button-container">
                                    <HelpfulLinks />
                                    <button
                                        aria-label="Dashboard Button"
                                        className="btn btn-primary m-2 color-override"
                                        onClick={dashboard}
                                        onKeyDown={dashboard}
                                        type="button"
                                    >
                                        <span className="nav-button-text">{globalDashboard}</span>
                                    </button>

                                    <button
                                        aria-label="Logout Button"
                                        className="btn btn-primary m-2 color-override"
                                        onClick={logout}
                                        onKeyDown={logout}
                                        type="button"
                                    >
                                        <span className="nav-button-text">{navLogout}</span>
                                    </button>
                                </div>
                                <div className="mobile-hamburger">
                                    <Hamburger />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="header-position-container">
                                    <h5>
                                        {category} at {diplayLocation()}
                                    </h5>
                                </div>
                                <div className="header-right" style={{ transform: "scale(0.7, 0.7)" }}>
                                    <div className="nav-button-container">
                                        <ResourcesLinks />
                                        <ActionButton
                                            aria-label="Exit Button"
                                            className="action-button-reg"
                                            onClick={closeTab}
                                        >
                                            <span className="nav-button-text">
                                                Dashboard
                                                <Icon
                                                    className="nav-button-icon"
                                                    fill="white"
                                                    iconName="HOME"
                                                    width={30}
                                                />
                                            </span>
                                        </ActionButton>
                                        <ActionButton
                                            aria-label="Logout Button"
                                            className="action-button-reg"
                                            onClick={logout}
                                        >
                                            <span className="nav-button-text">
                                                Logout
                                                <Icon
                                                    className="nav-button-icon"
                                                    fill="white"
                                                    iconName="LOGOUT"
                                                    width={30}
                                                />
                                            </span>
                                        </ActionButton>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </nav>
        </RbA>
    );
};

export default Header;
